import axios from "axios";
import store from "../../store";
import { API_URL } from "./constants";
import Actions from "../login/duck/actions";
import Operations from "../login/duck/operations";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react";
import { toRejection } from "./apiError";
const CancelToken = axios.CancelToken;
let source = CancelToken.source();

// Token renewal management to prevent concurrent renewal attempts
let tokenRenewalPromise = null;
let isRenewingToken = false;
let hasRenewalFailed = false; // Track if renewal has failed in this session
let failedRequests = new Set(); // Track failed request URLs to prevent retry loops

// Safe localStorage operations with error handling
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error("localStorage getItem error:", e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error("localStorage setItem error:", e);
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error("localStorage removeItem error:", e);
    }
  },
};

// Token validation helpers
const isValidToken = (token) => {
  if (!token || typeof token !== "string") return false;
  if (token === "undefined" || token === "null") return false;

  // Basic JWT format check (3 parts separated by dots)
  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Check each part is non-empty and base64-like
  return parts.every((part) => part && part.length > 0);
};

/**
 * Enhanced axios-retry configuration with exponential backoff
 */
axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay, // Implements exponential backoff
  retryCondition: (error) => {
    // Don't retry on authentication errors - let the interceptor handle it
    if (error.response && [401, 403].includes(error.response.status)) {
      return false;
    }

    // Don't retry token renewal requests
    if (error.config?.url?.includes("/auth/renew/")) {
      return false;
    }

    // Only retry on network errors and specific HTTP status codes
    const shouldRetry =
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response &&
        [429, 500, 502, 503, 504].includes(error.response.status));

    // Log retry attempts
    if (shouldRetry) {
      const endpoint =
        error.config.url?.split("/").pop()?.split("?")[0] || "unknown";
      console.info(
        `Will retry request to ${endpoint} (${
          error.response?.status || "network error"
        })`
      );
    }

    return shouldRetry;
  },
  onRetry: (retryCount, error, requestConfig) => {
    const endpoint =
      requestConfig.url?.split("/").pop()?.split("?")[0] || "unknown";
    console.info(`Retry attempt ${retryCount} for ${endpoint}`);

    // Track retries in Sentry
    if (retryCount === 1) {
      Sentry.withScope((scope) => {
        scope.setTag("retry", "true");
        scope.setTag("endpoint", endpoint);
        scope.setLevel("warning");
        Sentry.captureMessage(`API retry for ${endpoint}`);
      });
    }

    // If we got a 429 (Too Many Requests), add additional delay based on the Retry-After header
    if (error.response && error.response.status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      if (retryAfter) {
        const retryAfterMs = parseInt(retryAfter, 10) * 1000;
        console.info(`Rate limited, waiting ${retryAfter}s before retry`);
        return retryAfterMs;
      }
      // If no Retry-After header, use exponential backoff with jitter
      const baseDelay = Math.min(1000 * Math.pow(2, retryCount), 30000);
      const jitter = Math.random() * 0.3 * baseDelay; // 30% jitter
      const delay = baseDelay + jitter;
      console.info(`Rate limited, waiting ${Math.round(delay)}ms before retry`);
      return delay;
    }
  },
});

/**
 * Request interceptor to append Authorization Header
 */
axios.interceptors.request.use(
  (config) => {
    // Don't add auth header to renewal requests - they use refreshToken in payload
    const isRenewalRequest = config.url?.includes("/auth/renew/");

    // If renewal has failed, block all non-renewal requests to prevent loops
    if (hasRenewalFailed && !isRenewalRequest) {
      console.info("Blocking request after renewal failure:", config.url);
      return Promise.reject(
        new Error("Token renewal failed - blocking further requests")
      );
    }

    // Don't add auth header if renewal has failed or this is a renewal request
    if (!hasRenewalFailed && !isRenewalRequest) {
      const token = safeLocalStorage.getItem("accessToken");
      if (isValidToken(token)) {
        config.headers["Authorization"] = "Bearer " + token;
      }
    }
    config.cancelToken = source.token;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor to check auto renew token
 * in case access token has expired by using issued
 * refresh token
 */
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;
    const endpoint =
      originalRequest?.url?.split("/").pop()?.split("?")[0] || "unknown";
    const fullUrl = originalRequest?.url;

    // Track API errors in Sentry with appropriate context
    if (error?.response) {
      Sentry.withScope((scope) => {
        scope.setTag("endpoint", endpoint);
        scope.setTag("status", error.response.status);

        // Don't track expected client errors as exceptions:
        // - 401/403: Expected auth flows (token expired, unauthorized)
        // - 404: Expected for deactivated content (deleted profiles, banned Q&A, removed gigs, etc.)
        const expectedClientErrors = [401, 403, 404];
        if (!expectedClientErrors.includes(error.response.status)) {
          Sentry.captureException(error);
        }
      });
    }

    // Response-less errors ("Network Error", timeouts, cancellations) match
    // none of the auth branches below and used to escape as unhandled
    // rejections. Classify them here.
    if (!error?.response) {
      // Requests we aborted ourselves — a hard navigation (e.g. the WhatsApp
      // magic-link landing page) or a cancelled renewal. Not worth reporting,
      // so skip the Sentry capture below, but still reject: a promise that
      // never settles leaves its async frame alive and hangs any caller that
      // clears a spinner in a `finally`. Call sites that abort deliberately
      // should check `axios.isCancel(error)`.
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      // A genuine network failure. Capture with context for diagnosis.
      Sentry.withScope((scope) => {
        scope.setTag("endpoint", endpoint);
        scope.setTag("network_error", "true");
        scope.setLevel("warning");
        Sentry.captureException(error);
      });
    }

    // Handle token renewal failure
    if (
      error?.response &&
      error?.response.status === 401 &&
      error?.response?.data?.statusCode === 401 &&
      originalRequest.url === `${API_URL}/v1/auth/renew/`
    ) {
      hasRenewalFailed = true;
      failedRequests.clear(); // Clear failed requests set
      source.cancel("Token renewal failed");
      // Create new cancel token source for future requests
      source = CancelToken.source();

      console.info("Token renewal failed - logging out user");

      // Clear all auth data
      safeLocalStorage.removeItem("accessToken");
      safeLocalStorage.removeItem("refreshToken");

      store.dispatch(Actions.logout());
      window.location.reload();
      return Promise.reject(error);
    }

    // Handle authentication errors by attempting token renewal
    if (
      error?.response &&
      [401, 403].includes(error?.response.status) &&
      error?.response?.data?.statusCode
    ) {
      // Check if user is not logged in at all (no tokens)
      const accessToken = safeLocalStorage.getItem("accessToken");
      const refreshToken = safeLocalStorage.getItem("refreshToken");

      // If no tokens exist, this is an anonymous/public request - don't trigger auth flow
      if (!accessToken && !refreshToken) {
        return Promise.reject(error);
      }

      // If renewal has already failed in this session, don't retry
      if (hasRenewalFailed) {
        console.info("Token renewal already failed - preventing retry loop");
        return Promise.reject(error);
      }

      // Check if this specific request has already failed to prevent loops
      if (failedRequests.has(fullUrl)) {
        console.info(
          `Request to ${endpoint} already failed - preventing retry loop`
        );
        return Promise.reject(error);
      }

      // Add to failed requests set (with size limit to prevent memory leak)
      if (failedRequests.size >= 100) {
        failedRequests.clear();
        console.info("failedRequests Set cleared due to size limit");
      }
      failedRequests.add(fullUrl);

      // Check if already retried
      if (originalRequest._retry) {
        console.info(`Already retried ${endpoint} - preventing infinite loop`);
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Re-check refresh token after all the guards
      if (!isValidToken(refreshToken)) {
        console.info("No valid refresh token available - logging out");
        hasRenewalFailed = true;
        safeLocalStorage.removeItem("accessToken");
        safeLocalStorage.removeItem("refreshToken");
        store.dispatch(Actions.logout());
        window.location.reload();
        return Promise.reject(error);
      }

      // If token renewal is already in progress, wait for it
      if (isRenewingToken && tokenRenewalPromise) {
        console.info(
          `Token renewal already in progress for ${endpoint} - waiting...`
        );
        try {
          await tokenRenewalPromise;
          // After renewal completes, retry the original request
          originalRequest.headers["Authorization"] =
            "Bearer " + safeLocalStorage.getItem("accessToken");
          // Remove from failed requests since we're retrying
          failedRequests.delete(fullUrl);
          return await axios(originalRequest);
        } catch (renewalError) {
          return Promise.reject(renewalError);
        }
      }

      // Start new token renewal
      console.info(
        `Authentication error for ${endpoint} - initiating token renewal`
      );
      isRenewingToken = true;

      // Double-check refreshToken validity before making the request
      const currentRefreshToken = safeLocalStorage.getItem("refreshToken");
      if (!isValidToken(currentRefreshToken)) {
        console.info("Refresh token became invalid before renewal attempt");
        hasRenewalFailed = true;
        safeLocalStorage.removeItem("accessToken");
        safeLocalStorage.removeItem("refreshToken");
        store.dispatch(Actions.logout());
        window.location.reload();
        return Promise.reject(error);
      }

      tokenRenewalPromise = axios
        .post(
          `${API_URL}/v1/auth/renew/`,
          {
            refreshToken: currentRefreshToken,
          },
          {
            timeout: 15000, // 15 second timeout for renewal requests
          }
        )
        .then(async (res) => {
          if (res.status === 200) {
            // Validate response data structure to handle non-JSON responses
            if (!res.data || typeof res.data !== "object") {
              throw new Error("Invalid response format from renewal endpoint");
            }

            if (!res.data.accessToken || !res.data.refreshToken) {
              throw new Error("Missing tokens in renewal response");
            }

            console.info("Token renewed successfully");
            hasRenewalFailed = false; // Reset failure flag on success
            failedRequests.clear(); // Clear failed requests on successful renewal

            store.dispatch(
              Operations.loginUserSuccess(
                res.data.accessToken,
                res.data.refreshToken,
                res.data.profile,
                res.data?.isAccountReactivated || false
              )
            );

            // Update auth header
            axios.defaults.headers.common["Authorization"] =
              "Bearer " + res.data.accessToken;

            return res.data.accessToken;
          }
          throw new Error("Token renewal failed");
        })
        .catch((err) => {
          console.info("Token renewal failed", err);
          hasRenewalFailed = true;

          // Cancel all pending requests to prevent loops
          source.cancel("Token renewal failed");
          // Create new cancel token source for future requests
          source = CancelToken.source();

          // Clear all auth data
          safeLocalStorage.removeItem("accessToken");
          safeLocalStorage.removeItem("refreshToken");

          store.dispatch(Actions.logout());
          // Small delay before reload to prevent immediate retry storm
          setTimeout(() => window.location.reload(), 100);
          throw err;
        })
        .finally(() => {
          isRenewingToken = false;
          tokenRenewalPromise = null;
        });

      try {
        await tokenRenewalPromise;
        // Retry original request with new token
        originalRequest.headers["Authorization"] =
          "Bearer " + safeLocalStorage.getItem("accessToken");
        // Remove from failed requests since we're retrying
        failedRequests.delete(fullUrl);
        return await axios(originalRequest);
      } catch (renewalError) {
        return Promise.reject(renewalError);
      }
    }

    // For rate limit errors, add some useful debugging info
    if (error?.response?.status === 429) {
      console.warn(`Rate limited on ${endpoint} - Too many requests`);
      const retryAfter = error.response.headers["retry-after"];
      if (retryAfter) {
        console.warn(
          `Will retry after ${retryAfter}s according to Retry-After header`
        );
      }
    }

    // Reject with an `ApiError` that carries the response fields call sites
    // already read (`status`, `data`, `headers`, …) but is a real `Error`, so
    // uncaught rejections reach PostHog/Sentry with a type, message and stack
    // instead of "Object captured as exception with keys: config, data, …".
    // Response-less failures (network, timeout, cancel) pass through as-is.
    return Promise.reject(toRejection(error));
  }
);

/**
 * Reset axios state - useful for fresh starts after logout
 */
export const resetAxiosState = () => {
  hasRenewalFailed = false;
  isRenewingToken = false;
  tokenRenewalPromise = null;
  failedRequests.clear();
  source = CancelToken.source();
};

export default axios;
